import { useMemo } from 'react';
import type { NavEntitlement } from '@lucid/lego';
import type { FrontendResource } from '../api/services/lupp/user-identity/dto.user-identity';
import { useUserIdentity } from '@lucid/portal-core';

export type { NavEntitlement };

/**
 * Derives navigation entitlements from the frontendResources array returned
 * by the /me endpoint.
 *
 * The `feResourceCode` values in the DB are kebab-case and match NavEntitlement
 * IDs directly (e.g. "order-desk", "prospect-desk"). Inactive or deleted
 * resources are ignored. Duplicates are deduplicated via Set.
 */
export const deriveEntitlementsFromResources = (
  resources: FrontendResource[]
): NavEntitlement[] => {
  const result = new Set<NavEntitlement>();
  for (const resource of resources) {
    if (!resource.isActive || resource.isDeleted) continue;
    result.add(resource.feResourceCode as NavEntitlement);
  }
  return Array.from(result);
};

export type UseEntitlementsResult = {
  /** Navigation IDs the current user is allowed to see. Lucid Admin bypasses this entirely. */
  entitlements: NavEntitlement[];
  /** True while the /me endpoint is still loading */
  isLoading: boolean;
};

/**
 * Returns the navigation entitlements for the current user, derived from
 * the `role.frontendResources` array in the /me response.
 *
 * Falls back to an empty entitlements list while the identity is loading or
 * if the response has no role data (Lucid Admin bypass is handled upstream
 * in featureFlags.ts — nothing here changes).
 *
 * @component
 * @returns `{ entitlements, isLoading }`
 *
 * @example
 * ```tsx
 * const { entitlements } = useEntitlements();
 * // pass to isNavigationEnabled for role-driven nav filtering
 * ```
 */
export const useEntitlements = (): UseEntitlementsResult => {
  // PortalCoreProvider owns the single /me fetch via useUserIdentitySync.
  // We read directly from the portal store — no separate API call here.
  const { userIdentity, isReady } = useUserIdentity();

  const entitlements = useMemo(
    () => deriveEntitlementsFromResources(userIdentity?.role?.frontendResources ?? []),
    [userIdentity]
  );
  return { entitlements, isLoading: !isReady };
};
